import { Navigate, NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users as UsersIcon, ShieldCheck, History as HistoryIcon,
  Flag, AlertTriangle, Megaphone, CreditCard, Mail, MailQuestion,
  Sparkles, Plug, Download, Cog, Activity, LogOut, ShieldAlert,
} from 'lucide-react'
import { usePlatformAuth } from '../../contexts/PlatformAuthContext'
import clsx from 'clsx'

/**
 * Platform-admin SPA shell. Distinct from the operator AppLayout - gates
 * on PlatformAuthContext, lives at /admin/*, and styles itself with a
 * darker chrome so the admin / tenant operator surfaces don't get mistaken.
 */
const NAV = [
  { to: '/admin/dashboard',         label: 'Dashboard',       icon: LayoutDashboard },
  { to: '/admin/users',             label: 'Users',           icon: UsersIcon },
  { to: '/admin/roles',             label: 'Roles',           icon: ShieldCheck },
  { to: '/admin/audit',             label: 'Audit Log',       icon: HistoryIcon },
  { to: '/admin/feature-flags',     label: 'Feature Flags',   icon: Flag },
  { to: '/admin/maintenance',       label: 'Maintenance',     icon: AlertTriangle },
  { to: '/admin/announcements',     label: 'Announcements',   icon: Megaphone },
  { to: '/admin/subscriptions',     label: 'Subscriptions',   icon: CreditCard },
  { to: '/admin/email-templates',   label: 'Email Templates', icon: Mail },
  { to: '/admin/deliverability',    label: 'Deliverability',  icon: MailQuestion },
  { to: '/admin/ai-usage',          label: 'AI Usage',        icon: Sparkles },
  { to: '/admin/integrations',      label: 'Integrations',    icon: Plug },
  { to: '/admin/exports',           label: 'Exports',         icon: Download },
  { to: '/admin/health',            label: 'Health',          icon: Activity },
  { to: '/admin/settings',          label: 'Settings',        icon: Cog },
]

export default function AdminLayout() {
  const { admin, loading, logout } = usePlatformAuth()
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!admin) return <Navigate to="/admin/login" replace />

  const handleLogout = async () => {
    await logout()
    navigate('/admin/login')
  }

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Sidebar */}
      <aside className="w-60 min-h-screen bg-slate-950 flex flex-col flex-shrink-0">
        <div className="px-5 py-5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <ShieldAlert size={16} className="text-white" />
            </div>
            <div>
              <p className="font-display font-700 text-white text-base leading-tight">Platform</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Admin</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/5">
          <button onClick={handleLogout} className="sidebar-link-inactive w-full">
            <LogOut size={16} />
            Sign out
          </button>

          <div className="mt-3 px-3 py-2.5 rounded-lg bg-white/5 flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-amber-400">
                {admin.name?.[0]?.toUpperCase() ?? 'A'}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white truncate">{admin.name}</p>
              <p className="text-xs text-slate-500 truncate">
                {admin.is_super_admin ? 'super_admin' : (admin.role ?? 'admin')}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Topbar + content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-slate-700">Stovalt</span> · Platform Admin
          </p>
          <span className="text-xs text-slate-400">{admin.email}</span>
        </header>
        <main className="flex-1 p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

// Helper for sibling components - checks role list for at least one of `roles`.
export function adminHasRole(admin, roles) {
  if (!admin?.roles) return false
  return roles.some(r => admin.roles.includes(r))
}
